import { useEffect, useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import type { CompanyNavItem, CompanyState } from "../../types/company";
import { DEMO_COMPANY, newCompany } from "../../data/company";
import { loginHref } from "../../data/auth";
import { clearSession, getSession, logout as logoutApi } from "../../lib/auth";
import { toast } from "../../lib/toast";
import { activeListingCount, daysBetween, listingLimit, loadPricing, loadProfile, setBookings, setListings, usageLabel } from "../../lib/company";
import { ApiError } from "../../lib/api";
import { fetchBookings, fetchListings, getCompanyProfile, getSubscription } from "../../lib/companyApi";
import { useCompany } from "../../lib/companyContext";
import { longDate } from "../../lib/format";
import { TODAY } from "../../data/company";
import CompanyProvider from "./CompanyProvider";
import CompanyHeader from "./CompanyHeader";
import CompanySidebar from "./CompanySidebar";
import { useSeo } from "../../lib/seo";
import { ActionButton } from "./ui";
import { getDict, useT } from "../../i18n";

/** Header + sidebar shell for every /company page. */
export default function CompanyLayout() {
  // Profile and listings come from the API (see CompanyShell). Bookings and
  // drivers have no API yet and pointed at demo listings, so they start empty;
  // the demo subscription and invoices are kept.
  const [initialState] = useState<CompanyState>(() => {
    const session = getSession();
    return session?.verification === "pending"
      ? newCompany(session?.name ?? "", session?.email ?? "")
      : { ...DEMO_COMPANY, listings: [], units: [], bookings: [], drivers: [] };
  });

  return (
    <CompanyProvider initialState={initialState}>
      <CompanyShell />
    </CompanyProvider>
  );
}

interface Banner {
  tone: "amber" | "red" | "blue" | "grey";
  title: string;
  text: string;
  cta?: string;
  /** Where the button goes; the subscription page unless set. */
  href?: string;
}

const BANNER_TONES: Record<Banner["tone"], string> = {
  amber: "bg-tone-amber-bg text-[#5A3A00]",
  red: "bg-tone-red-bg text-[#7A1A10]",
  blue: "bg-tone-blue-bg text-[#1F3A86]",
  grey: "bg-white text-ink-soft",
};

/** Status banner from the design, most urgent first. */
function bannerFor(s: CompanyState): Banner | null {
  const b = getDict().company.banners;
  const sub = s.subscription;
  if (s.profile.verification === "incomplete") {
    return { tone: "blue", ...b.incomplete, href: "/company/profile" };
  }
  if (s.profile.verification === "pending") {
    return { tone: "amber", ...b.pending };
  }
  if (s.profile.verification === "rejected") {
    return { tone: "red", ...b.rejected, href: "/company/profile" };
  }
  // Pricing from the API: no subscription to manage yet (free launch, or plans coming)
  if (s.pricing) return null;
  if (!sub) {
    return { tone: "blue", ...b.noPlan };
  }
  if (sub.status === "past_due") {
    return { tone: "amber", title: b.pastDue.title, text: b.pastDue.text(longDate(sub.renewal)), cta: b.pastDue.cta };
  }
  if (sub.status === "trial" && sub.trialEnd) {
    return {
      tone: "blue",
      title: b.trial.title(daysBetween(TODAY, sub.trialEnd)),
      text: b.trial.text(longDate(sub.trialEnd)),
      cta: b.trial.cta,
    };
  }
  if (sub.status === "cancelled" || sub.status === "expired") {
    return { tone: "grey", title: b.inactive.title[sub.status], text: b.inactive.text, cta: b.inactive.cta };
  }
  return null;
}

function CompanyShell() {
  // Subscribes to the language so the banner and nav re-render on switch.
  const t = useT();
  // Account pages stay out of search results
  useSeo({ title: t.seo.companyArea, noindex: true, nofollow: true });
  const navigate = useNavigate();
  const { state, run } = useCompany();
  const banner = bannerFor(state);
  // Phones: sidebar panel opened from the header
  const [menuOpen, setMenuOpen] = useState(false);

  // Profile and listings come from the API; the rest of the company area is still demo data.
  useEffect(() => {
    Promise.all([
      getCompanyProfile().then((profile) => run(loadProfile, profile)),
      fetchListings().then(({ listings, units }) => run(setListings, listings, units)),
    ]).catch((err) => {
      toast.error(err);
      // Token invalid or refresh failed: log in again.
      if (err instanceof ApiError && err.status === 401) {
        clearSession();
        navigate(loginHref("company"));
      }
    });
    // Separate, so a pricing error doesn't hide the rest (and vice versa)
    getSubscription()
      .then((pricing) => run(loadPricing, pricing))
      .catch((err) => toast.error(err));
  }, [run, navigate]);

  // Bookings and messages: loaded now, then refreshed every 30 s so new
  // requests and replies show up without reloading the page
  useEffect(() => {
    let stopped = false;
    const load = () =>
      fetchBookings()
        .then((rows) => !stopped && run(setBookings, rows))
        .catch(() => {
          // Kept as is until the next refresh
        });
    load();
    const timer = setInterval(load, 30_000);
    return () => {
      stopped = true;
      clearInterval(timer);
    };
  }, [run]);
  const pendingCount = state.bookings.filter((b) => b.status === "pending").length;
  const unreadCount = state.bookings.reduce((n, b) => n + (b.unread ?? 0), 0);

  const nav: CompanyNavItem[] = [
    { key: "overview", href: "/company" },
    { key: "listings", href: "/company/listings" },
    { key: "bookings", href: "/company/bookings", count: pendingCount },
    { key: "calendar", href: "/company/calendar" },
    { key: "customers", href: "/company/customers" },
    { key: "messages", href: "/company/messages", count: unreadCount },
    { key: "reviews", href: "/company/reviews" },
    { key: "subscription", href: "/company/subscription" },
    {
      key: state.profile.ownerType === "individual" ? "accountProfile" : "profile",
      href: "/company/profile",
    },
  ];

  const logout = async () => {
    await logoutApi();
    toast.success(getDict().auth.toasts.loggedOut);
    navigate(loginHref("company"));
  };

  return (
    <div className="flex min-h-screen flex-col bg-surface">
      <CompanyHeader
        companyName={state.profile.name}
        verified={state.profile.verification === "verified"}
        onLogout={logout}
        menuOpen={menuOpen}
        onMenu={() => setMenuOpen(true)}
      />

      <div className="grid flex-1 grid-cols-1 content-start items-start lg:grid-cols-[232px_minmax(0,1fr)]">
        <CompanySidebar
          items={nav}
          usage={usageLabel(state)}
          activeListings={activeListingCount(state)}
          limit={listingLimit(state)}
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          companyName={state.profile.name}
        />

        <main className="w-full max-w-auto min-w-0 px-4 pt-5 pb-12 lg:px-9 lg:pt-7 lg:pb-16">
          {banner && (
            <div
              role="status"
              className={`mb-5 flex flex-wrap items-center gap-3.5 rounded-card px-4 py-3.5 ${BANNER_TONES[banner.tone]}`}
            >
              <div className="min-w-55 flex-1 text-sm leading-[1.45]">
                <b className="font-medium">{banner.title}</b> {banner.text}
              </div>
              {banner.cta && (
                <ActionButton variant="dark" onClick={() => navigate(banner.href ?? "/company/subscription")}>
                  {banner.cta}
                </ActionButton>
              )}
            </div>
          )}
          <Outlet />
        </main>
      </div>
    </div>
  );
}
